import b from 'b_';
import cx from 'classnames';
import React, {
    CSSProperties,
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { flushSync } from 'react-dom';

import { useResizeObserver } from '../../utils/hooks';
import useInViewport from '../../utils/hooks/useInViewport';

import './slider.scss';

interface Props {
    children: React.ReactNode[];
    shouldShowControls?: boolean;
    infinite?: boolean;
    autoSpinTimeout?: number;
    hideInactiveItems?: boolean;
    spaceBetweenItems?: number;
    shouldShowDots?: boolean;
    nextButton?: React.ReactNode;
    prevButton?: React.ReactNode;
    mix?: string;
    onSlideChange?: ({
        id,
        order,
        inViewport,
        isTrustedEvent,
    }: {
        id: string;
        order: number;
        inViewport: boolean;
        isTrustedEvent: boolean;
    }) => void;
    'aria-label'?: string;
}

interface ItemToRender {
    component: ReactNode;
    options: {
        ariaHide?: boolean;
    };
}

// eslint-disable-next-line complexity
export const Slider = (props: Props) => {
    const {
        children,
        shouldShowControls = true,
        infinite = false,
        hideInactiveItems = false,
        spaceBetweenItems = 0,
        shouldShowDots = false,
        autoSpinTimeout = 6000,
        nextButton,
        prevButton,
        onSlideChange,
        mix,
        'aria-label': ariaLabel,
    } = props;

    const isRepeating = infinite && children.length > 1;
    const startIndex = isRepeating ? 2 : 0;

    const contentWrapperRef = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(startIndex);
    const [touchPosition, setTouchPosition] = useState<number | null>(null);
    const [transitionEnabled, setTransitionEnabled] = useState(true);
    const [controlsIsActive, setControlsIsActive] = useState(true);
    const [bannerIsClickable, setBannerIsClickable] = useState(true);
    const [isTrustedEvent, setIsTrustedEvent] = useState(false);
    const { inViewport } = useInViewport(contentWrapperRef);
    const interval = useRef(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useResizeObserver(containerRef, () => {
        if (wrapperRef.current) {
            wrapperRef.current.style.width = '100%';
            const wrapperRectParams = wrapperRef.current.getBoundingClientRect();
            const roundedWidth = Math.floor(wrapperRectParams.width);
            wrapperRef.current.style.width = `${roundedWidth}px`;
        }
    });

    useEffect(() => {
        if (isRepeating) {
            if (currentIndex === 2 || currentIndex === children.length + 1) {
                setTransitionEnabled(true);
            }
        }
    }, [children.length, currentIndex, isRepeating]);

    const showNext = () => {
        setIsTrustedEvent(true);
        if (controlsIsActive && (isRepeating || currentIndex < children.length - 1)) {
            setCurrentIndex((prevState) => prevState + 1);
        }
    };

    const showPrev = () => {
        setIsTrustedEvent(true);
        if (controlsIsActive && (isRepeating || currentIndex > 0)) {
            setCurrentIndex((prevState) => prevState - 1);
        }
    };

    const startAutoSlide = useCallback(() => {
        if (isRepeating && !interval.current) {
            interval.current = window.setInterval(() => {
                setCurrentIndex((prevState) => prevState + 1);
            }, autoSpinTimeout);
        }
    }, [autoSpinTimeout, isRepeating]);

    const stopAutoSlide = () => {
        if (interval.current) {
            window.clearInterval(interval.current);
            interval.current = 0;
        }
    };

    const handleVisibilityChange = useCallback(() => {
        if (document.visibilityState === 'visible') {
            startAutoSlide();
        } else {
            stopAutoSlide();
        }
    }, [startAutoSlide]);

    useEffect(() => {
        if (document.visibilityState === 'visible') {
            startAutoSlide();
        }

        return stopAutoSlide;
    }, [startAutoSlide]);

    useEffect(() => {
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return function () {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [handleVisibilityChange, startAutoSlide]);

    const itemsToRender: ItemToRender[] = useMemo(() => {
        const childrenForRender: ItemToRender[] = children.map((child) => ({
            component: child,
            options: {},
        }));
        if (children.length > 1 && isRepeating) {
            const getInnerElem = (elem: ReactNode) => {
                return React.isValidElement(elem)
                    ? React.cloneElement(elem as ReactElement, {
                          tabIndex: -1,
                          role: 'presentation',
                          'aria-hidden': true,
                      })
                    : null;
            };
            const startFirstElem = getInnerElem(children[children.length - 2]);
            const startSecondElem = getInnerElem(children[children.length - 1]);
            const finishFirstElem = getInnerElem(children[0]);
            const finishSecondElem = getInnerElem(children[1]);
            // преобразуем children в более удобную для отображения структуру данных
            return [
                { component: startFirstElem, options: { ariaHide: true } },
                { component: startSecondElem, options: { ariaHide: true } },
                ...childrenForRender,
                { component: finishFirstElem, options: { ariaHide: true } },
                { component: finishSecondElem, options: { ariaHide: true } },
            ];
        }
        return childrenForRender;
    }, [children, isRepeating]);

    useEffect(() => {
        if (onSlideChange) {
            const index = isRepeating ? currentIndex : 0;
            const { id, order } = (itemsToRender?.[index]?.component as ReactElement)?.props || {};

            onSlideChange({
                id,
                order,
                inViewport,
                isTrustedEvent,
            });
            setIsTrustedEvent(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSlideChange, currentIndex, inViewport, isRepeating]);

    const handleNextClick = () => {
        showNext();
        setControlsIsActive(false);
    };

    const handlePrevClick = () => {
        showPrev();
        setControlsIsActive(false);
    };

    const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
        const touchDown = event.touches[0].clientX;
        setTouchPosition(touchDown);
        stopAutoSlide();
    };

    const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
        const touchDown = touchPosition;

        if (touchDown === null || !controlsIsActive) {
            return;
        }

        const currentTouch = event.touches[0].clientX;
        const diff = touchDown - currentTouch;

        if (diff > 5) {
            showNext();
            setControlsIsActive(false);
        }

        if (diff < -5) {
            showPrev();
            setControlsIsActive(false);
        }

        setTouchPosition(null);
    };

    const handleTouchEnd = () => {
        startAutoSlide();
    };

    const handleTransitionEnd = () => {
        if (isRepeating && (currentIndex === 1 || currentIndex === children.length + 2)) {
            flushSync(() => {
                setTransitionEnabled(false);
                setCurrentIndex(currentIndex === 1 ? children.length + 1 : 2);
            });
        }
        setControlsIsActive(true);
        setBannerIsClickable(true);
    };

    const handleMouseEnter = () => {
        stopAutoSlide();
    };

    const handleMouseLeave = () => {
        startAutoSlide();
    };

    const renderItems = () => {
        return itemsToRender.map(({ component: item, options: { ariaHide } }, index) => (
            <div
                className="slider__item-container"
                key={index}
                role={ariaHide ? 'presentation' : 'listitem'}
                style={{ '--marginright': spaceBetweenItems + 'px' } as CSSProperties}
            >
                <div
                    className="slider__item-cover"
                    hidden={index === currentIndex && bannerIsClickable}
                    onClick={() => {
                        setBannerIsClickable(false);
                        if (index > currentIndex) {
                            handleNextClick();
                        } else {
                            handlePrevClick();
                        }
                    }}
                />
                {item}
            </div>
        ));
    };

    const renderDots = () => {
        const dots: JSX.Element[] = [];

        for (let i = startIndex; i < startIndex + children.length; i = i + 1) {
            dots.push(
                <div className={b('slider__dot', { active: i === currentIndex })} onClick={() => setCurrentIndex(i)} />
            );
        }

        return dots;
    };

    return (
        <article
            aria-label={ariaLabel}
            className={cx('slider__container', mix)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            ref={containerRef}
        >
            <div className="slider__wrapper" ref={wrapperRef}>
                {shouldShowControls && (isRepeating || currentIndex > 0) && (
                    <div className="slider__prev-button-wrapper" onClick={handlePrevClick}>
                        {prevButton}
                    </div>
                )}
                <div
                    className={b('slider__content-wrapper', { 'active-item-only': hideInactiveItems })}
                    onTouchEnd={handleTouchEnd}
                    onTouchMove={handleTouchMove}
                    onTouchStart={handleTouchStart}
                    ref={contentWrapperRef}
                >
                    <div
                        className={b('slider__content', { animated: transitionEnabled })}
                        onTransitionEnd={() => handleTransitionEnd()}
                        role="list"
                        style={
                            children.length > 1
                                ? {
                                      transform: `translateX(calc(-${currentIndex * 100}% - ${
                                          spaceBetweenItems * currentIndex
                                      }px))`,
                                  }
                                : {}
                        }
                    >
                        {renderItems()}
                    </div>
                </div>
                {shouldShowControls && (isRepeating || currentIndex < children.length - 1) && (
                    <div className="slider__next-button-wrapper" onClick={handleNextClick}>
                        {nextButton}
                    </div>
                )}
            </div>
            {shouldShowDots && children.length > 1 && <div className="slider__dots-wrapper">{renderDots()}</div>}
        </article>
    );
};
